import { Point, Range } from 'slate';
import type { Editor } from 'slate';

import type { SuggestionUnit } from '../suggestion-units';
import { getSuggestionUnitRange } from '../suggestion-units';

export function getNextSuggestionUnit(
  suggestionUnits: SuggestionUnit[],
  selectedSuggestionGroupId: string | null,
  editor: Editor | null
): SuggestionUnit | null {
  if (suggestionUnits.length === 0) return null;
  const selectedSuggestionUnitIndex = getSelectedSuggestionUnitIndex(
    suggestionUnits,
    selectedSuggestionGroupId
  );

  if (selectedSuggestionUnitIndex === -1)
    return (
      getNextSuggestionUnitFromSelection(editor, suggestionUnits) ?? getFirstUnit(suggestionUnits)
    );

  if (selectedSuggestionUnitIndex >= suggestionUnits.length - 1)
    return getFirstUnit(suggestionUnits);

  return suggestionUnits[selectedSuggestionUnitIndex + 1] ?? null;
}

export function getPreviousSuggestionUnit(
  suggestionUnits: SuggestionUnit[],
  selectedSuggestionGroupId: string | null,
  editor: Editor | null
): SuggestionUnit | null {
  if (suggestionUnits.length === 0) return null;

  const selectedSuggestionUnitIndex = getSelectedSuggestionUnitIndex(
    suggestionUnits,
    selectedSuggestionGroupId
  );

  if (selectedSuggestionUnitIndex === -1)
    return (
      getPreviousSuggestionUnitFromSelection(editor, suggestionUnits) ??
      getLastUnit(suggestionUnits)
    );

  if (selectedSuggestionUnitIndex <= 0) return getLastUnit(suggestionUnits);

  return suggestionUnits[selectedSuggestionUnitIndex - 1] ?? null;
}

export function getSelectedSuggestionUnitIndex(
  suggestionUnits: SuggestionUnit[],
  selectedSuggestionGroupId: string | null
) {
  if (!selectedSuggestionGroupId) return -1;

  return suggestionUnits.findIndex(unit => unit.groupId === selectedSuggestionGroupId);
}

function getNextSuggestionUnitFromSelection(
  editor: Editor | null,
  suggestionUnits: SuggestionUnit[]
) {
  if (!editor?.selection) return null;

  const [, selectionEnd] = Range.edges(editor.selection);

  return (
    suggestionUnits.find(unit => {
      const edges = getSuggestionUnitRangeEdges(editor, unit);
      if (!edges) return false;

      return Point.equals(edges.start, selectionEnd) || Point.isAfter(edges.start, selectionEnd);
    }) ?? null
  );
}

function getPreviousSuggestionUnitFromSelection(
  editor: Editor | null,
  suggestionUnits: SuggestionUnit[]
) {
  if (!editor?.selection) return null;
  const [selectionStart] = Range.edges(editor.selection);

  for (let index = suggestionUnits.length - 1; index >= 0; index -= 1) {
    const unit = suggestionUnits[index];
    if (!unit) continue;

    const edges = getSuggestionUnitRangeEdges(editor, unit);
    if (!edges) continue;

    if (Point.equals(edges.end, selectionStart) || Point.isBefore(edges.end, selectionStart))
      return unit;
  }

  return null;
}

function getSuggestionUnitRangeEdges(editor: Editor, unit: SuggestionUnit) {
  const range = getSuggestionUnitRange(editor, unit);
  if (!range) return null;

  const [start, end] = Range.edges(range);
  return { end, start };
}

function getFirstUnit(suggestionUnits: SuggestionUnit[]) {
  return suggestionUnits[0] ?? null;
}

function getLastUnit(suggestionUnits: SuggestionUnit[]) {
  return suggestionUnits[suggestionUnits.length - 1] ?? null;
}
